import { FC } from 'react';
import { Grid, TextField, Fade, Box } from '@mui/material';
import { Loader, ModalSaveSection, Modal, ModalTitle } from '../../../../components';
import { IPunchlistDetail } from '../../../../models';
import { deepEqual } from 'fast-equals';
import { Formik, Form } from 'formik';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { updatePunchlist } from '../../../../fetch';

interface IPunchlistModalProps {
  open: boolean;
  handleClose: (refresh?: boolean) => void;
  punchlist: IPunchlistDetail;
}

const PunchlistModalSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  isArchived: Yup.boolean()
});

export const PunchlistModal: FC<IPunchlistModalProps> = ({ open, handleClose, punchlist }) => {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        name: punchlist?.name ?? '',
        isArchived: punchlist?.isArchived ?? false
      }}
      validationSchema={PunchlistModalSchema}
      onSubmit={async values => {
        try {
          await updatePunchlist(punchlist.punchListId, {
            name: values.name,
            description: punchlist.description,
            isArchived: values.isArchived
          });
          enqueueSnackbar(`Punchlist Updated!`, {
            variant: 'success'
          });
          handleClose(true);
        } catch (error) {
          enqueueSnackbar(error?.Detail ?? `Error saving punchlist, please try again`, {
            variant: 'error'
          });
        }
      }}
    >
      {({
        initialValues,
        isSubmitting,
        values,
        setFieldValue,
        handleSubmit,
        dirty,
        isValid,
        handleBlur,
        errors,
        touched,
        resetForm
      }) => {
        const handleCancel = () => {
          if (!deepEqual(initialValues, values)) {
            const result = window.confirm(
              'You have unsaved changes, are you sure you want to exit?'
            );
            if (result) {
              handleClose();
              resetForm();
            } else {
              return;
            }
          } else {
            handleClose();
          }
        };
        return (
          <Modal open={open} onClose={handleCancel} maxWidth="sm">
            <Form onSubmit={handleSubmit} autoComplete="none">
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <div>
                  <ModalTitle title="Edit Punchlist" />
                  <Box mt={1}>
                    <Grid container>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          required
                          variant="outlined"
                          autoComplete="nope"
                          label="Title"
                          name="title"
                          value={values.name}
                          onBlur={handleBlur}
                          size="small"
                          onChange={e => {
                            setFieldValue('name', e.target.value);
                          }}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  <ModalSaveSection
                    handleCancel={handleCancel}
                    isSaveDisabled={!dirty || isSubmitting || !isValid}
                  />
                </div>
              </Fade>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};
